import { filter, memoize, find, sortBy, uniqBy } from 'lodash';
import { Workbook, BookCode, BookParams, License, GradeLevel, AppStoreState } from 'types';
import { CurrentWorkbookTheme } from 'state/modules/lessonPlanner';
import { getLastPageForBookCode, SavedPageParams } from 'util/local-storage-util';
import Config from '../config';
import { translate } from '../translations/translator';

const translateNav = key => translate('nav', key);

type NextRouteInfo = {
  path: string;
  replace: boolean;
};

export function getRoutingForPage(
  newPageNumber: number,
  match: { params: Record<string, unknown>; url: string },
  withZoom = false,
  direct = false
): NextRouteInfo {
  const { bookCode, edition } = match.params;

  // Route to first page of pdf from cover
  if (Number.isNaN(newPageNumber)) {
    return {
      path: `${match.url}/0`,
      replace: false
    };
  }

  // Route to cover
  if (Number.isInteger(newPageNumber) && newPageNumber < 0) {
    return {
      path: `/books/${String(bookCode)}/${String(edition)}`,
      replace: false
    };
  }

  // Route to single page
  if (withZoom) {
    return {
      path: `/books/${String(bookCode)}/${String(edition)}/${newPageNumber}/zoom`,
      replace: false
    };
  }

  // Create path from scratch to be pushed to history
  if (direct) {
    return {
      path: `/books/${String(bookCode)}/${String(edition)}/${newPageNumber}`,
      replace: false
    };
  }

  // Turn the page
  return {
    path: String(newPageNumber),
    replace: true
  };
}

export function getRoutingForNewBookPage(
  PageNumber: number,
  params: Record<string, unknown>,
  newBookCode: BookCode
): NextRouteInfo {
  const { edition } = params;

  // Route to coresponding page in different book
  return {
    path: `/books/${String(newBookCode)}/${String(edition)}/${PageNumber}/zoom`,
    replace: true
  };
}

export function hasActiveTrialLicenses(licenses: License[]) {
  return licenses.find(license => license.licenseTypeId === 6 && license.licenses);
}

export function getWorkbookLink({ bookCode = '', edition }: BookParams) {
  return `/books/${bookCode}/${edition}`;
}
export function getBookPageLink(pageNumber: number, edition: string, bookCode: string) {
  return `/books/${String(bookCode)}/${String(edition)}/${pageNumber}/zoom`;
}

export function getClickAwayResourcesLink(edition: string, bookCode: string) {
  return `/books/${bookCode}/${edition}/resources/clickaways`;
}

export function getTeacherGuideLinkFor(name?: string) {
  return function (workbook: Workbook) {
    if (!workbook || !workbook.bookCode) return '';

    const tgBookCode =
      workbook.type === 'teachersGuide' || !workbook.teachersGuides || !workbook.teachersGuides[0]
        ? workbook.bookCode
        : workbook.teachersGuides[0].bookCode;
    const routing = `${name ? `/${name}` : ''}`;

    return `/books/${tgBookCode}/${workbook.edition}${routing}`;
  };
}

export function getGradeLevelDescription(gradeLevel: GradeLevel): string {
  return translateNav(`gradeLevel${gradeLevel}`);
}

export const getWorkbookTheme = memoize(
  (bookCode: string) =>
    ({ entity }: AppStoreState): CurrentWorkbookTheme => {
      const currentWorkbook = find(entity?.workbooksById, book => book.bookCode === bookCode);

      return {
        type: currentWorkbook?.type,
        primaryColor: currentWorkbook?.primaryColor,
        secondaryColor: currentWorkbook?.secondaryColor,
        textColor: currentWorkbook?.textColor
      };
    }
);

export const getWorkbook = (workbooks, path) => {
  if (!path) return null;
  const pathElements = path.split('/');

  const result = workbooks.find(
    x => pathElements.includes(x.bookCode) || pathElements.includes(`TG${x.bookCode}`)
  );

  return result;
};

export const isTeachersGuidePath = path => {
  if (!path) return false;
  return path.includes('/books/TG');
};

export const getSelectedWorkbook = (workbooks, bookCode) => {
  const selectedWorkbook = workbooks?.find(workbook => workbook.bookCode === bookCode);
  return selectedWorkbook || workbooks?.[0];
};

export const getStudentWorkbookCodeForNewLocale = (
  workbooks,
  productCurriculumId: number,
  newLocale: string
) => {
  const selectedWorkbook = filter(
    workbooks,
    workbook =>
      workbook.products[0].productCurriculumId === productCurriculumId &&
      workbook.locale === newLocale
  )[0];
  return selectedWorkbook ? selectedWorkbook.bookCode : null;
};

export function calculateMaxPDFPageWidth(
  clientWidth: number,
  clientHeight: number,
  isPortrait: boolean,
  isDoublePage: boolean
) {
  const maxPageWidth = clientWidth - 300; /*side spacing*/
  const maxPageHeight = clientHeight - (isPortrait ? 230 : 320);
  const PORTRAIT_RATIO = 0.78;
  const LANDSCAPE_RATIO = 1.3;
  const MAX_TOTAL_WIDTH = 2200;

  let pageWidth;
  let currentRatio = isPortrait ? PORTRAIT_RATIO : LANDSCAPE_RATIO;

  //if two page spread, have to double the ratio
  if (isDoublePage) currentRatio = currentRatio * 2;

  //Going full wide will cause the height to be too large
  if (maxPageWidth / currentRatio > maxPageHeight) {
    pageWidth = maxPageHeight * currentRatio;
  } //we can go wide as we want without blowing out the height (usually a landscape book)
  else {
    //max width is ok
    pageWidth = maxPageWidth;
  }
  //cap the largest we can wide
  pageWidth = pageWidth > MAX_TOTAL_WIDTH ? MAX_TOTAL_WIDTH : pageWidth;
  const pageHeight = pageWidth / currentRatio;

  return { pageWidth, pageHeight };
}
export function getNewLink(
  productCode: string,
  edition: string | number,
  route: string,
  userId?: number
) {
  const { pliUrl } = Config;
  const urlTemplates = {
    resources: `/books/${productCode}/${edition}/resources/`,
    clickAways: `/books/${productCode}/${edition}/resources/clickaways`,
    pinnedClickaways: `/books/${productCode}/${edition}/resources/pinned-clickaways`,
    assignmentTracker: `/books/${productCode}/${edition}/assignment-tracker`,
    lessonPlanner: `/books/${productCode}/${edition}/lesson-planner`,
    manageOptions: `/books/${productCode}/${edition}/lesson-planner/manage-options`,
    lnf: `/apps/letter-number-formations/${productCode}/${edition}`,
    wp: `/apps/wood-pieces/${productCode}/${edition}`,
    wdt: `/apps/wet-dry-try/${productCode}/${edition}`,
    wm: `/apps/worksheet-maker/${productCode}/${edition}`,
    pli: `${pliUrl}?app=hitt`,
    support: `/books/${productCode}/${edition}/support`,
    home: `/home/${productCode}/${edition}`
  };

  if (route === 'workbook') {
    const { pageNumber, singlePageView }: SavedPageParams = getLastPageForBookCode(
      userId,
      productCode
    );

    return `/books/${productCode}/${edition}${
      pageNumber != null && pageNumber > 0 ? '/' + pageNumber : ''
    }${singlePageView ? '/zoom' : ''}`;
  } else if (route === 'teachersGuide') {
    const { pageNumber, singlePageView } = getLastPageForBookCode(userId, 'TG' + productCode);
    return `/books/TG${productCode}/${edition}${
      pageNumber != null && pageNumber > 0 ? '/' + pageNumber : ''
    }${singlePageView ? '/zoom' : ''}`;
  }

  return urlTemplates[route];
}

export const findWorkbook = ({ workbooks, bookCode, edition }) =>
  workbooks.some(
    x =>
      (x.bookCode === `TG${bookCode}` || x.bookCode === bookCode) && String(x.edition) === edition
  );

export const isRouteValid = ({ bookCode, edition, pageNumber, workbooks }) => {
  try {
    const foundWorkbook = findWorkbook({ bookCode, edition, workbooks });

    // 'pageNumber' can be undefined for the book cover
    return foundWorkbook && (pageNumber === undefined || !isNaN(parseInt(pageNumber, 10)));
  } catch (err) {
    return false;
  }
};

export const getGroupedResources = pagesDetails => {
  return pagesDetails?.reduce((acc, { resourceCategories }) => {
    resourceCategories?.map(({ value: category, resources, clickAways }) => {
      if (!resources.length && !clickAways.length) return null;
      acc[category] = uniqBy(
        sortBy(
          [...(acc[category] ?? []), ...(resources as any[]), ...(clickAways as any[])],
          'title'
        ),
        'id'
      );
      return acc[category];
    });
    return acc;
  }, {});
};

export const getWorkbookTourModalCSS = position => {
  const caratPositionY =
    position?.orientation === 'above'
      ? {
          bottom: -15,
          borderTop: '20px solid white'
        }
      : {
          top: -15,
          borderBottom: '20px solid white'
        };

  return {
    position: 'absolute',
    ...(position?.offset ?? {}),
    '&:after': {
      content: "''",
      position: 'absolute',
      borderLeft: '20px solid transparent',
      borderRight: '20px solid transparent',
      width: 0,
      transform: 'translateX(20px)',
      right: `${position?.caratPos}%`,
      ...caratPositionY
    }
  };
};
